import React, { useState, useEffect, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './HeldOrdersPage.module.css';
import {
  getHeldOrders,
  releaseHeldOrder,
  unholdHeldOrder,
  cancelHeldOrder,
} from '../api/salesRequestApi';
import AppLayout from '../components/AppLayout';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

const translations = {
  en: {
    heldOrdersTitle: 'Held Orders',
    manageHeldOrdersText:
      'Monitor held sales requests and decide whether to release, unhold, or cancel them.',
    noHeldRequests: 'No held sales requests at the moment.',
    requestId: 'Request ID',
    itemsLabel: 'Request Items',
    product: 'Product',
    sort: 'Sort',
    color: 'Color',
    quantity: 'Quantity',
    submissionDate: 'Submitted On',
    salesUser: 'Sales User',
    customer: 'Customer',
    customerId: 'Customer ID',
    action: 'Action',
    actions: 'Actions',
    releaseNow: 'Release',
    unhold: 'Unhold',
    cancel: 'Cancel',
    noActionsForAdd: 'No actions available for add requests.',
    failedFetch: 'Failed to fetch held orders. Please try again.',
    releaseOnlyRemove: 'Only remove requests can be released.',
    unholdOnlyRemove: 'Only remove requests can be unheld.',
    cancelOnlyRemove: 'Only remove requests can be cancelled.',
    confirmReleaseTitle: 'Confirm Release',
    confirmUnholdTitle: 'Confirm Unhold',
    confirmCancelTitle: 'Confirm Cancel',
    confirmReleaseMsg: 'Are you sure you want to release this held order now?',
    confirmUnholdMsg: 'Are you sure you want to unhold this held order?',
    confirmCancelMsg: 'Are you sure you want to cancel this held order?',
    reasonLabel: 'Reason for cancellation',
    reasonPlaceholder: 'Enter reason here...',
    reasonRequired: 'Cancellation reason is required.',
    heldOrderReleased: 'Held order released successfully.',
    failedRelease: 'Failed to release held order. Please try again.',
    heldOrderUnheld: 'Held order unheld successfully.',
    failedUnhold: 'Failed to unhold held order. Please try again.',
    heldOrderCancelled: 'Held order cancelled successfully.',
    failedCancel: 'Failed to cancel held order. Please try again.',
    close: 'Close',
    unknownUser: 'Unknown user',
    homepage: 'Homepage',
  },
  ar: {
    heldOrdersTitle: 'الطلبات المعلقة',
    manageHeldOrdersText:
      'راقب طلبات المبيعات المعلقة وقرر ما إذا كنت ستصدرها أو تلغي تعليقها أو تلغيها.',
    noHeldRequests: 'لا توجد طلبات مبيعات معلقة حاليًا.',
    requestId: 'رقم الطلب',
    itemsLabel: 'العناصر في الطلب',
    product: 'المنتج',
    sort: 'الصنف',
    color: 'اللون',
    quantity: 'الكمية',
    submissionDate: 'تاريخ التقديم',
    salesUser: 'مندوب المبيعات',
    customer: 'العميل',
    customerId: 'رقم العميل',
    action: 'الإجراء',
    actions: 'الإجراءات',
    releaseNow: 'إصدار',
    unhold: 'إلغاء التعليق',
    cancel: 'إلغاء',
    noActionsForAdd: 'لا توجد إجراءات متاحة لطلبات الإضافة.',
    failedFetch: 'فشل في جلب الطلبات المعلقة. حاول مرة أخرى.',
    releaseOnlyRemove: 'يمكن إصدار الطلبات من نوع إزالة فقط.',
    unholdOnlyRemove: 'يمكن إلغاء تعليق الطلبات من نوع إزالة فقط.',
    cancelOnlyRemove: 'يمكن إلغاء الطلبات من نوع إزالة فقط.',
    confirmReleaseTitle: 'تأكيد الإصدار',
    confirmUnholdTitle: 'تأكيد إلغاء التعليق',
    confirmCancelTitle: 'تأكيد الإلغاء',
    confirmReleaseMsg: 'هل أنت متأكد من أنك تريد إصدار هذا الطلب الآن؟',
    confirmUnholdMsg: 'هل أنت متأكد أنك تريد إلغاء تعليق هذا الطلب؟',
    confirmCancelMsg: 'هل أنت متأكد أنك تريد إلغاء هذا الطلب؟',
    reasonLabel: 'سبب الإلغاء',
    reasonPlaceholder: 'أدخل السبب هنا...',
    reasonRequired: 'سبب الإلغاء مطلوب.',
    heldOrderReleased: 'تم إصدار الطلب المعلق بنجاح.',
    failedRelease: 'فشل في إصدار الطلب المعلق. حاول مرة أخرى.',
    heldOrderUnheld: 'تم إلغاء تعليق الطلب بنجاح.',
    failedUnhold: 'فشل في إلغاء تعليق الطلب. حاول مرة أخرى.',
    heldOrderCancelled: 'تم إلغاء الطلب المعلق بنجاح.',
    failedCancel: 'فشل في إلغاء الطلب المعلق. حاول مرة أخرى.',
    close: 'إغلاق',
    unknownUser: 'مستخدم غير معروف',
    homepage: 'الصفحة الرئيسية',
  },
};

const ModalTypes = {
  NONE: null,
  RELEASE: 'release',
  UNHOLD: 'unhold',
  CANCEL: 'cancel',
};

const HeldOrdersPage = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  const [heldOrders, setHeldOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');

  const [confirmModalType, setConfirmModalType] = useState(ModalTypes.NONE);
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  useEffect(() => {
    fetchHeldOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const fetchHeldOrders = async () => {
    try {
      const response = await getHeldOrders();
      setHeldOrders(response.data);
    } catch (error) {
      console.error('Error fetching held orders:', error);
      setMessage(t('failedFetch'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const openReleaseModal = (id, action) => {
    if (action !== 'remove') {
      setMessage(t('releaseOnlyRemove'));
      setMessageType('error');
      return;
    }
    setConfirmModalType(ModalTypes.RELEASE);
    setConfirmOrderId(id);
  };

  const openUnholdModal = (id, action) => {
    if (action !== 'remove') {
      setMessage(t('unholdOnlyRemove'));
      setMessageType('error');
      return;
    }
    setConfirmModalType(ModalTypes.UNHOLD);
    setConfirmOrderId(id);
  };

  const openCancelModal = (id, action) => {
    if (action !== 'remove') {
      setMessage(t('cancelOnlyRemove'));
      setMessageType('error');
      return;
    }
    setConfirmModalType(ModalTypes.CANCEL);
    setConfirmOrderId(id);
  };

  const handleReleaseConfirm = async () => {
    try {
      await releaseHeldOrder(confirmOrderId);
      setMessage(t('heldOrderReleased'));
      setMessageType('success');
      setHeldOrders((prev) => prev.filter((o) => o._id !== confirmOrderId));
    } catch (error) {
      console.error('Error releasing held order:', error);
      setMessage(t('failedRelease'));
      setMessageType('error');
    }
    closeConfirmModal();
  };

  const handleUnholdConfirm = async () => {
    try {
      await unholdHeldOrder(confirmOrderId);
      setMessage(t('heldOrderUnheld'));
      setMessageType('success');
      setHeldOrders((prev) => prev.filter((o) => o._id !== confirmOrderId));
    } catch (error) {
      console.error('Error unholding held order:', error);
      setMessage(t('failedUnhold'));
      setMessageType('error');
    }
    closeConfirmModal();
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) {
      setMessage(t('reasonRequired'));
      setMessageType('error');
      return;
    }
    try {
      await cancelHeldOrder(confirmOrderId, { reason: cancelReason });
      setMessage(t('heldOrderCancelled'));
      setMessageType('success');
      setHeldOrders((prev) => prev.filter((o) => o._id !== confirmOrderId));
    } catch (error) {
      console.error('Error cancelling held order:', error);
      setMessage(t('failedCancel'));
      setMessageType('error');
    }
    closeConfirmModal();
  };

  const closeConfirmModal = () => {
    setConfirmModalType(ModalTypes.NONE);
    setConfirmOrderId(null);
    setCancelReason('');
  };

  const closeMessageBanner = () => {
    setMessage(null);
  };

  const formatDate = (value) => {
    return formatDateDDMMYYYY(value, '-');
  };

  return (
    <AppLayout
      title={t('heldOrdersTitle')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.heldOrdersPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {!loading && (
        <>
          <section className={styles.heroCard}>
            <p>{t('manageHeldOrdersText')}</p>
          </section>

          {message && (
            <div
              className={`${styles.alert} ${
                messageType === 'error' ? styles.alertError : styles.alertSuccess
              }`}
              role="alert"
            >
              <span>{message}</span>
              <button type="button" className={styles.alertDismiss} onClick={closeMessageBanner}>
                {t('close')}
              </button>
            </div>
          )}

          {heldOrders.length === 0 ? (
            <div className={styles.emptyState}>{t('noHeldRequests')}</div>
          ) : (
            <section className={styles.ordersGrid}>
              {heldOrders.map((order) => (
                <article key={order._id} className={styles.orderCard}>
                  <div className={styles.cardHeader}>
                    <span className={styles.requestId}>{t('requestId')}: {order.requestId}</span>
                    <div className={styles.metaRow}>
                      <span className={styles.metaChip}>
                        {t('salesUser')}: {order.salesUser?.username || t('unknownUser')}
                      </span>
                      {order.customerSnapshot && (
                        <span className={styles.metaChip}>
                          {t('customer')}: {order.customerSnapshot.fullName || t('unknownUser')}
                        </span>
                      )}
                      {order.customerSnapshot?.customerID && (
                        <span className={styles.metaChip}>
                          {t('customerId')}: {order.customerSnapshot.customerID}
                        </span>
                      )}
                      <span className={styles.metaChip}>{t('action')}: {order.action}</span>
                      <span className={styles.metaChip}>{t('submissionDate')}: {formatDate(order.createdAt)}</span>
                    </div>
                  </div>

                  <div className={styles.itemsList}>
                    <span className={styles.fieldLabel}>{t('itemsLabel')}</span>
                    {(order.items || []).map((item, idx) => (
                      <div key={`${order._id}-item-${idx}`} className={styles.itemRow}>
                        <span><span className={styles.itemLabel}>{t('product')}:</span> {item.product}</span>
                        <span><span className={styles.itemLabel}>{t('sort')}:</span> {item.sortName}</span>
                        <span><span className={styles.itemLabel}>{t('color')}:</span> {item.color}</span>
                        <span><span className={styles.itemLabel}>{t('quantity')}:</span> {item.quantity}</span>
                      </div>
                    ))}
                  </div>

                  <span className={styles.actionNote}>{t('actions')}</span>
                  <div className={styles.actionButtons}>
                    {order.action !== 'add' ? (
                      <>
                        <button
                          className={styles.primaryButton}
                          onClick={() => openReleaseModal(order._id, order.action)}
                        >
                          {t('releaseNow')}
                        </button>
                        <button
                          className={styles.warningButton}
                          onClick={() => openUnholdModal(order._id, order.action)}
                        >
                          {t('unhold')}
                        </button>
                        <button
                          className={styles.dangerButton}
                          onClick={() => openCancelModal(order._id, order.action)}
                        >
                          {t('cancel')}
                        </button>
                      </>
                    ) : (
                      <span className={styles.actionNote}>{t('noActionsForAdd')}</span>
                    )}
                  </div>
                </article>
              ))}
            </section>
          )}
        </>
      )}

      {confirmModalType !== ModalTypes.NONE && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            {confirmModalType === ModalTypes.RELEASE && (
              <>
                <h2>{t('confirmReleaseTitle')}</h2>
                <p>{t('confirmReleaseMsg')}</p>
                <div className={styles.modalButtonGroup}>
                  <button className={styles.modalPrimary} onClick={handleReleaseConfirm}>
                    {t('releaseNow')}
                  </button>
                  <button className={styles.modalSecondary} onClick={closeConfirmModal}>
                    {t('close')}
                  </button>
                </div>
              </>
            )}

            {confirmModalType === ModalTypes.UNHOLD && (
              <>
                <h2>{t('confirmUnholdTitle')}</h2>
                <p>{t('confirmUnholdMsg')}</p>
                <div className={styles.modalButtonGroup}>
                  <button className={styles.modalPrimary} onClick={handleUnholdConfirm}>
                    {t('unhold')}
                  </button>
                  <button className={styles.modalSecondary} onClick={closeConfirmModal}>
                    {t('close')}
                  </button>
                </div>
              </>
            )}

            {confirmModalType === ModalTypes.CANCEL && (
              <>
                <h2>{t('confirmCancelTitle')}</h2>
                <p>{t('confirmCancelMsg')}</p>
                <label className={styles.formLabel} htmlFor="cancel-reason">
                  {t('reasonLabel')}
                </label>
                <textarea
                  id="cancel-reason"
                  className={styles.formInput}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={t('reasonPlaceholder')}
                />
                <div className={styles.modalButtonGroup}>
                  <button className={styles.modalPrimary} onClick={handleCancelConfirm}>
                    {t('cancel')}
                  </button>
                  <button className={styles.modalSecondary} onClick={closeConfirmModal}>
                    {t('close')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default HeldOrdersPage;
